import { useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useEnhancedDashboardData } from '@/hooks/useEnhancedDashboardData'
import { KPIRow } from '@/components/dashboard/kpi'
import { PeriodSelector, GroupingSelector } from '@/components/dashboard/filters'
import type { DatePeriod, Grouping } from '@/components/dashboard/filters'
import { DualAxisChart, HorizontalBarChart } from '@/components/dashboard/charts'
import type { DistributionDataItem } from '@/components/dashboard/charts'
import { TabbedSection } from '@/components/dashboard/tabs'
import { TabbedVisitorSection } from '@/components/dashboard/TabbedVisitorSection'
import { ProfileMetrics, getBestConverting, getWorstConverting } from '@/components/dashboard/ProfileMetrics'
import { ActiveFilterBanner } from '@/components/dashboard/ActiveFilterBanner'
import { features } from '@/config/features'
import type { DrillDownFilter, DrillDownFilterType } from '@/types/filters'

export default function DashboardPage() {
  const { site } = useAuthStore()
  const [period, setPeriod] = useState<DatePeriod>('last30d')
  const [grouping, setGrouping] = useState<Grouping>('daily')
  const [drillDownFilter, setDrillDownFilter] = useState<DrillDownFilter | null>(null)

  const {
    kpis,
    timeSeries,
    distributions,
    visitors,
    isLoading,
  } = useEnhancedDashboardData(site?.id, period, grouping, drillDownFilter)

  // Handler for distribution chart clicks
  const handleDistributionClick = useCallback(
    (type: DrillDownFilterType, item: DistributionDataItem) => {
      setDrillDownFilter({
        type,
        value: item.label,
        label: item.label,
      })
    },
    []
  )

  // Handler for visitor table clicks
  const handleVisitorClick = useCallback((visitorHash: string, dogName: string) => {
    setDrillDownFilter({
      type: 'visitor_hash',
      value: visitorHash,
      label: dogName,
    })
  }, [])

  // Clear filter
  const clearFilter = useCallback(() => {
    setDrillDownFilter(null)
  }, [])

  // No site configured
  if (!isLoading && !site) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No site configured</h2>
        <p className="text-gray-600 mb-4">Add your website to start tracking analytics.</p>
        <Link to="/settings" className="btn-primary">
          Add your site
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">{site?.domain}</p>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod} />
          <GroupingSelector value={grouping} onChange={setGrouping} />
        </div>
      </div>

      {/* Active Filter Banner */}
      {drillDownFilter && (
        <ActiveFilterBanner
          filter={drillDownFilter}
          onClear={clearFilter}
          isEmpty={!isLoading && kpis.visitors === 0}
        />
      )}

      {/* KPI Row */}
      <KPIRow data={kpis} isLoading={isLoading} />

      {/* Main Chart */}
      <div className="section-card">
        <div className="section-header">
          <span>📊</span>
          <span>Overview</span>
        </div>
        <div className="p-4">
          <DualAxisChart data={timeSeries} isLoading={isLoading} />
        </div>
      </div>

      {/* Four Section Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Traffic Sources */}
        <TabbedSection
          title="Traffic Sources"
          icon="📡"
          tabs={[
            { id: 'referrer', label: 'Referrer' },
            { id: 'campaign', label: 'Campaign' },
            { id: 'keyword', label: 'Keyword' },
            { id: 'entry', label: 'Entry Page' },
          ]}
          defaultTab="referrer"
        >
          {(activeTab) => {
            switch (activeTab) {
              case 'referrer':
                return (
                  <HorizontalBarChart
                    data={distributions.referrers}
                    iconType="source"
                    onItemClick={(item) => handleDistributionClick('referrer_source', item)}
                  />
                )
              case 'campaign':
                return (
                  <HorizontalBarChart
                    data={distributions.campaigns}
                    iconType="none"
                    onItemClick={(item) => handleDistributionClick('utm_campaign', item)}
                  />
                )
              case 'keyword':
                return (
                  <HorizontalBarChart
                    data={distributions.keywords}
                    iconType="none"
                    onItemClick={(item) => handleDistributionClick('utm_term', item)}
                  />
                )
              case 'entry':
                return (
                  <HorizontalBarChart
                    data={distributions.entryPages}
                    iconType="path"
                    onItemClick={(item) => handleDistributionClick('entry_page', item)}
                  />
                )
              default:
                return null
            }
          }}
        </TabbedSection>

        {/* Geography */}
        <TabbedSection
          title="Geography"
          icon="🌍"
          tabs={[
            { id: 'country', label: 'Country' },
            { id: 'region', label: 'Region' },
            { id: 'city', label: 'City' },
          ]}
          defaultTab="country"
        >
          {(activeTab) => {
            switch (activeTab) {
              case 'country':
                return (
                  <HorizontalBarChart
                    data={distributions.countries}
                    iconType="country"
                    onItemClick={(item) => handleDistributionClick('country_code', item)}
                  />
                )
              case 'region':
                return (
                  <HorizontalBarChart
                    data={distributions.regions}
                    iconType="none"
                  />
                )
              case 'city':
                return (
                  <HorizontalBarChart
                    data={distributions.cities}
                    iconType="none"
                    onItemClick={(item) => handleDistributionClick('city', item)}
                  />
                )
              default:
                return null
            }
          }}
        </TabbedSection>

        {/* Technology */}
        <TabbedSection
          title="Technology"
          icon="💻"
          tabs={[
            { id: 'browser', label: 'Browser' },
            { id: 'os', label: 'OS' },
            { id: 'device', label: 'Device' },
          ]}
          defaultTab="browser"
        >
          {(activeTab) => {
            switch (activeTab) {
              case 'browser':
                return (
                  <HorizontalBarChart
                    data={distributions.browsers}
                    iconType="browser"
                    onItemClick={(item) => handleDistributionClick('browser', item)}
                  />
                )
              case 'os':
                return (
                  <HorizontalBarChart
                    data={distributions.operatingSystems}
                    iconType="os"
                    onItemClick={(item) => handleDistributionClick('os', item)}
                  />
                )
              case 'device':
                return (
                  <HorizontalBarChart
                    data={distributions.devices}
                    iconType="device"
                    onItemClick={(item) => handleDistributionClick('device_type', item)}
                  />
                )
              default:
                return null
            }
          }}
        </TabbedSection>

        {/* Profiles - Side by Side (hidden when revenue features disabled) */}
        {features.profiles && (
          <div className="section-card">
            <div className="section-header">
              <span>👤</span>
              <span>Profiles</span>
            </div>
            {kpis.revenue > 0 ? (
              <div className="grid md:grid-cols-2 divide-x divide-gray-100">
                {/* ICP Column */}
                <ProfileMetrics
                  title="Ideal Client Profile"
                  subtitle="Highest converting segments"
                  headerIcon="🤑"
                  variant="positive"
                  categories={[
                    { icon: '🌍', label: 'Location', item: getBestConverting(distributions.countries) },
                    { icon: '📱', label: 'Device', item: getBestConverting(distributions.devices) },
                    { icon: '🌐', label: 'Browser', item: getBestConverting(distributions.browsers) },
                    { icon: '📡', label: 'Source', item: getBestConverting(distributions.referrers) },
                    { icon: '📢', label: 'Campaign', item: getBestConverting(distributions.campaigns) },
                  ]}
                />
                {/* Low Converting Column */}
                <div className="bg-red-50/30">
                  <ProfileMetrics
                    title="Low-Converting Profile"
                    subtitle="Lowest converting segments"
                    headerIcon="🚽"
                    variant="negative"
                    categories={[
                      { icon: '🌍', label: 'Location', item: getWorstConverting(distributions.countries) },
                      { icon: '📱', label: 'Device', item: getWorstConverting(distributions.devices) },
                      { icon: '🌐', label: 'Browser', item: getWorstConverting(distributions.browsers) },
                      { icon: '📡', label: 'Source', item: getWorstConverting(distributions.referrers) },
                      { icon: '📢', label: 'Campaign', item: getWorstConverting(distributions.campaigns) },
                    ]}
                  />
                </div>
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                  <span className="text-3xl grayscale opacity-50">📊</span>
                </div>
                <h3 className="text-lg font-medium text-gray-400 mb-2">No Conversions Tracked Yet</h3>
                <p className="text-sm text-gray-400 max-w-md mx-auto">
                  Profile analysis requires conversion data to identify your ideal customer segments.
                  Once revenue events are tracked, ICP and low-converting profiles will appear here.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Visitor Tables with Tabs */}
      <TabbedVisitorSection
        visitors={visitors}
        isLoading={isLoading}
        onVisitorClick={handleVisitorClick}
      />
    </div>
  )
}
